// Telemetría de errores cliente — ligera, en-house, opt-in via VITE_TELEMETRY_URL.
// Filosofía IKOGNIT: cero datos personales, cero IKO, cero pseudónimos, cero IPs propias.
// Solo: timestamp, mensaje sanitizado, stack truncado, ruta (#hash), userAgent.
// Si no hay endpoint configurado, queda en console.warn.

import { API_BASE } from './api';
import { getPlatform, isTablet } from './platform';

interface TelemetryEvent {
  ts: number;
  msg: string;
  stack?: string;
  url: string;
  ua: string;
  appVersion: string;
  // Plataforma: imprescindible para depurar fallos que SOLO pasan en iPad
  // (rechazo Apple 2026-07-13) y no en iPhone ni navegador.
  platform?: string;
  tablet?: boolean;
  w?: number;
  h?: number;
}

// Una sola fuente de verdad: la versión de package.json (inyectada por vite).
const APP_VERSION = __APP_VERSION__;
// Por defecto reportamos a NUESTRO backend (log a stdout, sin BD ni datos personales).
// Antes esto quedaba vacío → los errores solo iban a console.warn y no veíamos NADA
// de lo que le pasaba al iPad de App Review.
const ENDPOINT = (import.meta as any).env?.VITE_TELEMETRY_URL || `${API_BASE}/diag/client-error`;
const MAX_STACK = 2000;
const SEND_QUEUE: TelemetryEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function sanitize(msg: string): string {
  // Quita posibles tokens base64 largos, UUIDs, claves hex.
  return msg
    .replace(/[A-Za-z0-9+/=_-]{40,}/g, '<redacted>')
    .replace(/[0-9a-fA-F]{32,}/g, '<redacted>')
    .slice(0, 500);
}

function enqueue(ev: TelemetryEvent) {
  SEND_QUEUE.push(ev);
  if (SEND_QUEUE.length > 50) SEND_QUEUE.splice(0, SEND_QUEUE.length - 50);
  if (!ENDPOINT) {
    // Sin endpoint: log al console (DevTools) y fuera.
    console.warn('[telemetry]', ev.msg, ev.stack || '');
    return;
  }
  if (flushTimer) return;
  flushTimer = setTimeout(flush, 3000);
}

function flush() {
  flushTimer = null;
  if (!ENDPOINT || SEND_QUEUE.length === 0) return;
  const batch = SEND_QUEUE.splice(0);
  try {
    const body = JSON.stringify({ events: batch });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
    }
  } catch {
    /* tragado a propósito */
  }
}

export function captureException(err: unknown, context?: string) {
  const e = err as Error;
  const msg = sanitize((context ? `[${context}] ` : '') + (e?.message || String(err)));
  const stack = e?.stack ? sanitize(e.stack).slice(0, MAX_STACK) : undefined;
  let platform = 'unknown'; let tablet = false;
  try { platform = getPlatform(); tablet = isTablet(); } catch { /* nunca romper por telemetría */ }
  enqueue({
    ts: Date.now(),
    msg,
    stack,
    url: location.hash || location.pathname,
    ua: navigator.userAgent.slice(0, 200),
    appVersion: APP_VERSION,
    platform,
    tablet,
    w: window.innerWidth,
    h: window.innerHeight,
  });
}

export function installGlobalErrorHandlers() {
  window.addEventListener('error', (e) => {
    captureException(e.error || new Error(e.message), 'window.onerror');
  });
  window.addEventListener('unhandledrejection', (e) => {
    captureException(e.reason || new Error('unhandled rejection'), 'unhandledrejection');
  });
  window.addEventListener('beforeunload', flush);
}
